import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { faro } from '@grafana/faro-react';
import { countriesGlobalsSelector } from 'redux/globals/selectors';
import { useSelector } from 'react-redux';
import { authProfile } from 'redux/auth/selectors';
import dayjs from 'dayjs';

// Types for navigation metadata
interface NavigationMetadata {
  fromPath: string;
  timestamp: string;
  appVersion: string;
  country: string;
}

const NavigationTracker: React.FC = () => {
  const location = useLocation();

  const profile = useSelector(authProfile);
  const countries = useSelector(countriesGlobalsSelector);
  const country = useMemo(() => {
    if (countries)
      return (
        countries.find(
          (c: { id: number; name: string }) => c.id === profile?.countryID,
        )?.name ?? 'Desconocido'
      );
    return 'Desconocido';
  }, [countries, profile]);

  // Track previous pathname (without query parameters)
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    // Get pathname without query parameters
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    // Only track if pathname actually changed (ignore query parameter changes)
    if (currentPath !== previousPath) {
      // Create metadata for the navigation
      const metadata: NavigationMetadata = {
        fromPath: previousPath,
        timestamp: dayjs().toISOString(),
        appVersion: process.env.REACT_APP_VERSION || 'Desconocido',
        country,
      };

      try {
        // Send flattened event to Faro
        const eventAttributes = {
          type: 'user_navigation',
          source: 'click',
          ...metadata,
        };

        faro?.api?.pushEvent('page_view', eventAttributes, 'webapp-ppoint');
      } catch (error) {
        // Silently handle errors to avoid disrupting user experience
        console.error('Error tracking navigation event:', error);
      }

      // Update previous path reference
      previousPathRef.current = currentPath;
    }
  }, [location.pathname, country]); // Only depend on pathname, not search or hash

  // Self-closing component - renders nothing
  return null;
};

export default NavigationTracker;
